/**
 * fetchShepherdRisksRaw — a Shepherd-only, resilient reader for the Asana tasks S3 export.
 *
 * WHY THIS EXISTS (and is separate from the shared `fetchS3Data`):
 * The pipeline currently writes the Asana `custom_fields` value with double-escaped inner
 * quotes (`\\"` instead of `\"`), so every task row that HAS custom fields is invalid JSON.
 * The shared `fetchS3Data` parses an object line-by-line and returns `[]` for the WHOLE
 * object if any single line throws — which would (and did) blank out the entire dataset.
 *
 * Rather than change that shared reader (used by every OE dataset), this fetcher is used
 * ONLY by the Shepherd hook. It lists the same objects via `s3ListBlobs`, then parses each
 * line defensively: on a parse failure it applies a narrowly-targeted repair (`\\"` → `\"`)
 * and retries that one line; anything still unparseable is skipped and logged. Well-formed
 * lines are never modified. Nothing shared is touched, so no other dataset's behaviour
 * changes. This is a stopgap for the upstream escaping bug and can be retired once the
 * pipeline emits valid JSON (the repair then simply never matches).
 */
import { logger } from 'src/logger';
import { s3ListBlobs } from 'src/utilities/CommonUtilities';

/** A single S3 object command as returned by `s3ListBlobs` (loose — we only `send` it). */
interface S3ObjectListing {
  client: { send: (command: unknown) => Promise<{ Body?: { transformToString: (enc: string) => Promise<string> } }> };
  commandList: unknown[];
}

/**
 * Repair the known upstream corruption: inner quotes double-escaped as `\\"` become `\"`.
 * Only ever applied to a line that already FAILED to parse, so valid lines are untouched.
 */
const repairDoubleEscapedQuotes = (line: string): string => line.split('\\\\"').join('\\"');

/** Parse one JSONL object body into rows, repairing/skipping malformed lines. */
const parseBodyResilient = (body: string, keyForLog: string): unknown[] => {
  const rows: unknown[] = [];
  let skipped = 0;
  body
    .split('\n')
    .filter((l) => l.trim())
    .forEach((line) => {
      try {
        rows.push(JSON.parse(line));
        return;
      } catch {
        // fall through to repair attempt
      }
      try {
        rows.push(JSON.parse(repairDoubleEscapedQuotes(line)));
      } catch {
        skipped += 1;
      }
    });
  if (skipped > 0) {
    logger.warn(`fetchShepherdRisksRaw: skipped ${skipped} unparseable line(s) in ${keyForLog}`);
  }
  return rows;
};

/** Read + resiliently parse every object under a prefix; concatenated rows. */
const readPrefixResilient = async (prefix: string): Promise<unknown[]> => {
  const listing = (await s3ListBlobs(prefix)) as S3ObjectListing | undefined;
  if (!listing || !listing.commandList.length) return [];

  // A read failure (download / transformToString) is propagated, not swallowed: returning
  // [] would be indistinguishable from a genuinely empty dataset, so the hook would report
  // "finished" with zero risks and cache that empty result for the full TTL. Throwing lets
  // the caller surface the error state and skip caching, matching the shared `useS3Dataset`.
  // Note this is all-or-nothing: one failed object fails the whole prefix. Malformed *lines*
  // are still tolerated (repaired/skipped) by `parseBodyResilient` — only I/O errors throw.
  const perObject = await Promise.all(
    listing.commandList.map(async (command, i) => {
      try {
        const response = await listing.client.send(command);
        const body = (await response?.Body?.transformToString('utf-8')) || '';
        return parseBodyResilient(body, `${prefix}[${i}]`);
      } catch (err) {
        logger.error(`fetchShepherdRisksRaw: failed to read an object under ${prefix}`, err as Error);
        throw err instanceof Error ? err : new Error(String(err));
      }
    })
  );

  return perObject.flat();
};

export interface ShepherdRisksRawResult {
  rows: unknown[];
  lastUpdatedAt: string | null;
}

// Module-level cache + inflight dedup, mirroring `useS3Dataset` so mounting the section
// (or remounting on navigation) doesn't re-list and re-download the whole Asana export
// each time. Keyed by the data prefix; TTL matches the shared hook's default (5 min).
const CACHE_TTL_MS = 5 * 60 * 1000;
interface CacheEntry {
  result: ShepherdRisksRawResult;
  expiresAt: number;
}
const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<ShepherdRisksRawResult>>();

/** Test-only: reset the module-level cache + inflight maps. */
export const __resetShepherdRisksCache = (): void => {
  cache.clear();
  inflight.clear();
};

/**
 * Fetch the raw Shepherd task rows + last-updated timestamp from S3, tolerating the
 * malformed `custom_fields` lines. Shepherd-only — does not go through `getS3Data`.
 *
 * Cached per data prefix (5-min TTL) with inflight dedup, so repeated mounts reuse the
 * result instead of re-downloading the whole export. Pass `forceRefresh` to bypass the
 * cache (e.g. a manual refetch).
 */
export const fetchShepherdRisksRaw = async (dataPrefix: string, lastUpdatedPrefix: string, forceRefresh = false): Promise<ShepherdRisksRawResult> => {
  if (!forceRefresh) {
    const hit = cache.get(dataPrefix);
    if (hit && hit.expiresAt > Date.now()) return hit.result;
    const pending = inflight.get(dataPrefix);
    if (pending) return pending;
  }

  const run = (async (): Promise<ShepherdRisksRawResult> => {
    const [rows, lastUpdatedRows] = await Promise.all([readPrefixResilient(dataPrefix), readPrefixResilient(lastUpdatedPrefix)]);
    // last-updated objects are a single `{ last_updated_at }` row; take the first if present.
    const first = lastUpdatedRows[0] as { last_updated_at?: string } | undefined;
    const result: ShepherdRisksRawResult = { rows, lastUpdatedAt: first?.last_updated_at ?? null };
    cache.set(dataPrefix, { result, expiresAt: Date.now() + CACHE_TTL_MS });
    return result;
  })();

  inflight.set(dataPrefix, run);
  run
    .finally(() => {
      if (inflight.get(dataPrefix) === run) inflight.delete(dataPrefix);
    })
    .catch(() => {});
  return run;
};
